import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, Send, Trash2, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/Logo";
import api, { API, refreshSession } from "@/lib/api";
import { t, lang } from "@/i18n";

const SUGGESTIONS = [
  t("copilot.quel_est_mon_plat_le_plus_rentable"),
  t("copilot.quels_plats_ont_les_marges_les_plus_faible"),
  t("copilot.quels_ingredients_ont_le_plus_augmente_ce"),
  t("copilot.quels_plats_devrais_je_augmenter_en_prix"),
  t("copilot.quelles_recettes_deviennent_moins_rentable"),
];

const post = (text) => fetch(`${API}/copilot/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", "X-Lang": lang }, body: JSON.stringify({ text }) });

function Bubble({ m, idx }) {
  const mine = m.role === "user";
  return (
    <div className={`flex gap-2 fade-up sm:gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={mine ? `copilot-user-message-${idx}` : "ai-copilot-response-message"}>
      <div className={`hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:flex ${mine ? "bg-foreground text-background" : "bg-[hsl(var(--ai)/0.12)] text-[hsl(var(--ai))]"}`}>
        {mine ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </div>
      <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed sm:max-w-[85%] sm:px-4 sm:py-3 ${mine ? "bg-foreground text-background" : "min-w-0 overflow-x-auto border bg-card"}`}>
        {mine ? m.content : m.content ? <div className="prose-chat"><ReactMarkdown>{m.content}</ReactMarkdown></div> : <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
    </div>
  );
}

export default function Copilot() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/copilot/history").then(({ data }) => setMsgs((m) => (m.length ? m : data))); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);

  const send = async (q) => {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    const append = (d) => setMsgs((m) => {
      const c = [...m];
      const last = c[c.length - 1];
      if (last?.role === "assistant") c[c.length - 1] = { ...last, content: last.content + d };
      else c.push({ role: "assistant", content: d });
      return c;
    });
    try {
      let res = await post(question);
      if (res.status === 401) { await refreshSession(); res = await post(question); }
      if (res.status === 402) {
        const detail = (await res.json().catch(() => ({}))).detail;
        if (detail?.code === "quota_exceeded") window.dispatchEvent(new CustomEvent("quota:exceeded", { detail }));
      }
      if (!res.ok) throw new Error();
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) append(ev.delta);
          if (ev.error) append(ev.error);
        }
      }
    } catch {
      toast.error(t("copilot.le_copilote_n_a_pas_pu_repondre_reessayez"));
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => { await api.delete("/copilot/history"); setMsgs([]); };

  return (
    <div className="flex h-[calc(100dvh-10.5rem-env(safe-area-inset-bottom)-env(safe-area-inset-top))] flex-col lg:h-[calc(100dvh-5rem)]">
      <PageHeader eyebrow={t("copilot.connecte_a_vos_donnees")} title={t("common.copilote_ia")} description={t("copilot.posez_vos_questions_sur_la_rentabilite_de")}
        actions={msgs.length > 0 && <Button variant="outline" size="sm" onClick={clear} data-testid="copilot-clear-btn"><Trash2 className="mr-2 h-4 w-4" />{t("copilot.effacer")}</Button>} />
      <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain rounded-2xl border bg-card/40 p-3 sm:p-6" data-testid="copilot-messages">
        {msgs.length === 0 && (
          <div className="flex min-h-full flex-col items-center justify-center text-center">
            <div className="mb-4 rounded-2xl bg-[hsl(var(--ai)/0.12)] p-4"><Sparkles className="h-6 w-6 text-[hsl(var(--ai))]" /></div>
            <p className="text-base font-semibold sm:text-lg">{t("copilot.comment_puis_je_ameliorer_votre_marge")}</p>
            <div className="mt-5 flex w-full max-w-2xl flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} data-testid={`copilot-suggestion-${i}`} className="rounded-xl border bg-background px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:border-[hsl(var(--ai)/0.5)] hover:text-foreground active:bg-muted sm:rounded-full sm:py-1.5">{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} idx={i} />)}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex items-end gap-2">
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("copilot.votre_question")} className="min-h-[44px] resize-none text-base md:text-sm" data-testid="ai-copilot-chat-input" />
        <Button type="submit" size="icon" className="h-11 w-11 shrink-0" disabled={busy || !text.trim()} data-testid="ai-copilot-send-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
